import parse from 'date-fns/parse'
import { getIn } from 'timm'
import readXlsxFile, { readSheetNames } from 'read-excel-file'
import { availablityStatusTypes } from 'design-framework/common/constants'
import defaultDFtheme from 'design-framework/utils/base-ui-theme'
import LogHelper from './logger'

const logger = LogHelper('design-framework:helpers')

export const excludedListingTypes = ['concerts', 'festival', 'exhibition']

export const aliasTypes = {
	sport: 'sports',
	specials: 'special',
}

const supportsPassive = () => {
	let isPassive = false
	const opts = Object.defineProperty({}, 'passive', {
		// eslint-disable-next-line
		get: () => {
			isPassive = true
		},
	})
	window.addEventListener('test', null, opts)

	return isPassive
}

const hrefHandler = href => param => {
	if (typeof href === 'function') href(param)
	else if (typeof href === 'string' && href.length > 0) {
		if (typeof window !== 'undefined') {
			logger.log('Going to redirect', href)
			window.location.href = href
		}
	} else return null
}

const getTabbable = element => {
	const tabbableElements = [
		'a[href]',
		'link[href]',
		'button',
		'input:not([type="hidden"])',
		'select',
		'textarea',
		'[tabIndex]:not([tabIndex="-1"])',
	]

	return Array.prototype.slice.call(
		element.querySelectorAll(tabbableElements.join(', ').trim())
	)
}

const stripHttp = url => url.replace(/^(https:|http:)/g, '')
export { supportsPassive, hrefHandler, getTabbable, stripHttp }

/**
 *  Adjust and fallsback to events as the eventType where-ever needed.
 * @param {*} eventType
 */
export function adjustEventType(eventType = 'events') {
	let targetEventType = eventType
	// check if it matches our aliases, if yes, substitute them/
	if (aliasTypes[eventType]) targetEventType = aliasTypes[eventType]

	if (excludedListingTypes.indexOf(targetEventType) > -1) {
		targetEventType = 'events'
	}

	return targetEventType
}
/**
 * Converts a string to titlecase. Words without separation needs to have _ with them
 * item => Item
 * item_details => Item Details
 * ITEM DETAILS => Item Details
 *
 * @param {string} [value='']
 */
export const convertToTitleCase = (value = '') =>
	(value || '').replace(/\w\S*/g, txt => {
		const Pieces =
			(txt.indexOf('_') > -1 &&
				txt.split('_').map(piece => convertToTitleCase(piece))) ||
			[]

		return Pieces.length > 0
			? Pieces.join('')
			: txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
	})

/**
 * Convert 24 hour format to 12 hour format
 */

export function to12HrFormat(time) {
	if (!time) return ''
	// Check correct time format and split into components
	let parsedTime = time
		.toString()
		.match(/^([01]\d|2[0-3])(:)?([0-5]\d)$/) || [time]

	if (parsedTime.length > 1) {
		// If time format correct
		parsedTime = parsedTime.slice(1) // Remove full string match value
		parsedTime[5] = +parsedTime[0] < 12 ? ' AM' : ' PM' // Set AM/PM
		parsedTime[0] = +parsedTime[0] % 12 || 12 // Adjust hours
		parsedTime[1] = ':'
	}

	return parsedTime.join('') // return adjusted time or original string
}

/**
 * @description Generate Youtube HD Thumbnail URL
 * @param {String} url
 * @return {String} Youtube Thumbnail URL
 */
export function youtubeThumbNailGenrator(url) {
	const youtube = url.split('/')

	return `https://img.youtube.com/vi/${
		youtube[youtube.length - 1]
	}/hqdefault.jpg`
}

export function pick(obj, keys = []) {
	return Object.assign({}, ...keys.map(key => ({ [key]: obj[key] })))
}

export const convertREDateFormatToStandardDate = date => {
	if (!date) {
		return null
	}

	return parse(date, 'dd-MM-yyyy', new Date())
}

export const convertREDateTimeFormatToStandardDateTime = date => {
	if (!date) {
		return null
	}

	return parse(date, 'dd-MM-yyyy hh:mm:ss a', new Date())
}

export const youtubeEmbedURLGenerator = url => {
	const patt = /[\\?&]v=([^&#]*)/g
	const result = patt.exec(url)

	if (result) {
		return `https://youtube.com/embed/${result[1]}`
	}

	return url
}

export const colorDescriptionMap = (theme = defaultDFtheme) => {
	return {
		[availablityStatusTypes.FAST_FILLING]: {
			color: theme.colors.orange,
			borderColor: theme.colors.orange,
		},
		[availablityStatusTypes.FILLING_FAST]: {
			color: theme.colors.orange,
			borderColor: theme.colors.orange,
		},
		[availablityStatusTypes.SOLD_OUT]: {
			color: theme.colors.lightGrey3,
			borderColor: theme.colors.lightGrey3,
		},
		[availablityStatusTypes.AVAILABLE]: {
			color: theme.colors.bmsGreen,
			borderColor: theme.colors.bmsGreen,
		},
	}
}

/**
 * util for adding event listerners with detach inbuilt.
 * @param {string} event
 * @param {function} cb
 */
export const addEvent = (dom, event, cb) => {
	// if there is no window available, then mock it up
	if (!dom || !dom.addEventListener) {
		return () => {}
	}

	dom.addEventListener(
		event,
		cb,
		supportsPassive() ? { passive: true, capture: true } : true
	)

	return () =>
		dom.removeEventListener(
			event,
			cb,
			supportsPassive() ? { passive: true, capture: true } : true
		)
}

/**
 * Simple delay creator
 */
export const sleep = delay =>
	new Promise(resolve => {
		setTimeout(resolve, delay)
	})

/**
 * Simple default back handler based on the scope by identifying if app or if there is history
 */
export const tempHandleDefaultBack = goToUrl => {
	const targetUrl = typeof goToUrl === 'string' && goToUrl
	// Navigate to App Native Listing Page
	// Domain name
	const globalVar = (typeof window !== 'undefined' && window) || {}
	if (typeof window !== 'undefined') {
		// check if there is history associated

		if (!targetUrl && window.history && window.history.length > 1) {
			window.history.back()

			return
		}
	}
	if (
		getIn(globalVar.webkit, [
			'messageHandlers',
			'didReceiveCallback',
			'postMessage',
		])
	) {
		// Trigger calls to send message to webviews iOS
		try {
			// IOS Trigger
			globalVar.webkit.messageHandlers.didReceiveCallback.postMessage(
				'goBack'
			)
		} catch (err) {
			logger.error(
				`${err}There was some problem sending message to native app - IOS`
			)
		}
	} else if (getIn(globalVar.BMS_ANDROID, ['handleWebViewFlowDialog'])) {
		// Trigger calls to send message to webviews Andriod
		try {
			// Android Trigger
			globalVar.BMS_ANDROID.handleWebViewFlowDialog('goBack')
		} catch (err) {
			logger.error(
				`${err}There was some problem sending message to native app - ANDRIOD`
			)
		}
	}
}

export const handleDefaultBack = goToUrl => {
	const targetUrl = typeof goToUrl === 'string' && goToUrl
	// Navigate to App Native Listing Page
	// Domain name
	const globalVar = (typeof window !== 'undefined' && window) || {}

	if (
		getIn(globalVar.webkit, [
			'messageHandlers',
			'didReceiveCallback',
			'postMessage',
		])
	) {
		// Trigger calls to send message to webviews iOS
		try {
			// IOS Trigger
			globalVar.webkit.messageHandlers.didReceiveCallback.postMessage(
				'goBack'
			)
		} catch (err) {
			logger.error(
				`${err}There was some problem sending message to native app - IOS`
			)
		}
	} else if (getIn(globalVar.BMS_ANDROID, ['handleWebViewFlowDialog'])) {
		// Trigger calls to send message to webviews Andriod
		try {
			// Android Trigger
			globalVar.BMS_ANDROID.handleWebViewFlowDialog('goBack')
		} catch (err) {
			logger.error(
				`${err}There was some problem sending message to native app - ANDRIOD`
			)
		}
	} else if (typeof window !== 'undefined') {
		// check if there is history associated

		if (!targetUrl && window.history && window.history.length > 1) {
			window.history.back()

			return
		}
		window.location.replace(targetUrl || '/')
	}
}

/**
 * truncation of the sentence based on the number given
 */

export const handleCustomEllipsis = (text, charLimit) => {
	if (text.length > charLimit) {
		const truncatedText = `${text.substring(0, charLimit)}...`

		return truncatedText
	}

	return text
}

const slugifyRegex = /[^a-z0-9A-Z\- ]/g
// eslint-disable-next-line no-useless-escape
const slugifyWithAllowedUrl = /[^a-z0-9A-Z\-._~:/?#\[\]@!$&'()*+,;= ]/g
const spaceRegex = /[ ]/g
/**
	@function slugify
		@param {string} str String to convert
		@param {string} retainUrlFormat Whether slashes should be retained
		@returns {string}
	@description Returns a slug of the input string
*/
export function slugify(str = '', retainUrlFormat = false) {
	if (typeof str !== 'string') return str

	return str
		.trim()
		.replace(retainUrlFormat ? slugifyWithAllowedUrl : slugifyRegex, '')
		.replace(spaceRegex, '-')
		.toLowerCase()
}

/**
 * Check if element is visible in the dom
 *
 * @param {*} ele
 * @returns
 */
export function isVisible(ele) {
	if (!ele || !ele.clientHeight) return false

	return (
		ele.clientWidth !== 0 &&
		ele.clientHeight !== 0 &&
		ele.style.opacity !== 0
	)
}

export function isEmptyObject(obj) {
	for (const key in obj) if (obj.hasOwnProperty(key)) return false // eslint-disable-line no-prototype-builtins

	return true
}

export const googleMapsUrl = ({ latitude, longitude }) =>
	latitude && longitude
		? `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
		: ''

const convertCsvtoArray = async file => {
	const fileData = await new Promise(resolve => {
		const reader = new FileReader()

		reader.onload = e => {
			const rawData = e.target.result
			const arrayData = rawData.split('\n').map(row => row.split(','))

			return resolve(arrayData)
		}

		reader.readAsBinaryString(file)
	})

	return fileData
}

const convertXlsxToArray = async (file, sheetName) => {
	const tempFileData = []

	await readXlsxFile(file, { sheet: sheetName }).then(rows => {
		tempFileData.push(...rows)
	})

	return tempFileData
}

export const convertFileDataToArray = async event => {
	const file = event?.target?.files[0] || {}
	const fileName = file?.name || ''
	const fileType = fileName?.split('.')[1] || ''
	let fileData = []

	if (fileType) {
		if (fileType === 'xlsx') {
			let sheetNames = []

			await readSheetNames(file).then(names => {
				sheetNames = [...names]
			})

			if (sheetNames.length > 1) {
				await Promise.all(
					sheetNames.map(async sheetName => {
						const tempFileData = await convertXlsxToArray(
							file,
							sheetName
						)
						fileData.push([...tempFileData])
					})
				)
			} else {
				await readXlsxFile(file).then(rows => {
					fileData.push(...rows)
				})
			}
		} else {
			fileData = await convertCsvtoArray(file)
		}
	}

	return fileData
}
